/**
 * Copy a string to the OS clipboard with a graceful degradation
 * path. Callers should treat the returned promise as best-effort:
 * resolves true on success, false on total failure — never rejects.
 *
 * Why not just navigator.clipboard.writeText?
 * - It requires a "focused document" — modals whose backdrop
 *   steals focus can trigger a NotAllowedError even in a click
 *   handler
 * - It requires a secure context (https / localhost); some
 *   preview / staging setups on plain http would fail silently
 * - Some embedded webviews strip the Clipboard API entirely
 *
 * Fallback uses the deprecated document.execCommand('copy') via a
 * hidden textarea. Ugly, but works in every browser that has a
 * synchronous click handler on the caller's stack (which all our
 * "copy this" buttons do).
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Preferred path — modern browsers with the Clipboard API in
  // a secure context.
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path — most common failure
      // mode is a NotAllowedError from a focus-stealing backdrop
      // or a document not marked as secure.
    }
  }

  // Legacy path — hidden textarea + execCommand. Works from any
  // click-initiated code path even without Clipboard permissions.
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Fixed position + zero opacity keeps the textarea from
  // scrolling the page or being visible to the user.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  // iOS Safari needs a range selection, not just .select(), for
  // execCommand('copy') to see the value. Wrap both to cover.
  let ok = false;
  try {
    textarea.focus();
    textarea.select();
    if (textarea.setSelectionRange) {
      textarea.setSelectionRange(0, textarea.value.length);
    }
    // execCommand is deprecated but still shipping in every current
    // browser. Its removal has been "coming soon" for years; we
    // switch off it the day the last supported target drops it.
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    textarea.remove();
  }
  return ok;
}
